import { Link } from 'react-router-dom';
import { EuStrip } from '@/features/common/layout/EuStrip';
import { SITE_PAGE } from '@/features/common/layout/publicPageLinks';
import { publicLayout } from '@/i18n/dictionaries/publicLayout';
import { useTranslation } from '@/i18n/useTranslation';
import '@/features/common/layout/footer.css';
import '@/features/common/layout/site-header.css';

/** One footer column, so the four of them cannot drift in markup or labelling. */
function FooterColumn({ heading, links }: { heading: string; links: [string, string][] }) {
  return (
    <nav aria-label={heading}>
      <h3>{heading}</h3>
      {links.map(([to, label]) => (
        <Link key={to + label} to={to}>
          {label}
        </Link>
      ))}
    </nav>
  );
}

/** Official service marks. Raster where no vector was supplied — kept as delivered. */
const SERVICE_MARKS: [string, string][] = [
  ['/img/gov/mpass.svg', 'M-Pass'],
  ['/img/gov/msign.svg', 'M-Sign'],
  ['/img/gov/mconnect.png', 'M-Connect'],
  ['/img/gov/mnotify.png', 'M-Notify'],
  ['/img/gov/mlog.png', 'M-Log'],
  ['/img/gov/mcloud.png', 'MCloud'],
];

const SOCIAL: [string, string, string][] = [
  [
    'https://www.facebook.com/minister.agricultura.industrie.alimentara/',
    'Facebook',
    '/img/social/facebook.svg',
  ],
  ['https://www.instagram.com/maiagovmd/', 'Instagram', '/img/social/instagram.svg'],
  ['https://www.youtube.com/@ministerulagriculturiimd', 'YouTube', '/img/social/youtube.svg'],
  // LinkedIn is deliberately absent, not forgotten: MAIA has no page yet, and a link that goes
  // nowhere is a WCAG 2.1 AA link-purpose defect against an accessibility budget of "fewer than
  // five issues" (ToR p. 29-30). The asset is already at /img/social/linkedin.svg — add the row
  // here once there is a URL for it.
];

/**
 * The public site footer: brand blurb, four link columns, service marks, agency logos, social,
 * the EU visibility strip, MUD attribution, and the policy row.
 *
 * Every label comes from the message bundle the Thymeleaf fragment used, so the wording a
 * ministry already publishes is carried across rather than re-authored. The two exceptions are
 * the postal address and phone number, which the template spelled out in Romanian; they are in
 * the dictionary now so the English page stops printing a Romanian street type.
 */
export function SiteFooter() {
  const { t } = useTranslation(publicLayout);

  return (
    <footer className="site-footer">
      <div className="container">
        <div className="site-footer__grid">
          <div className="footer-brand">
            <div className="footer-brand__row">
              {/* The header's own mark, not a lookalike: same classes, so the tile and its
                  hover reveal have one implementation. aria-hidden because the wordmark
                  beside it already says AKIS. */}
              <span className="brand__mark footer-brand__mark" aria-hidden="true">
                <span className="brand__mark-text">AK</span>
              </span>
              <span className="footer-brand__word">
                <strong>AKIS</strong>
                <span>{t('footerTagline')}</span>
              </span>
            </div>
            <p>{t('footerDescription')}</p>
            <div className="footer-chips">
              {SERVICE_MARKS.map(([src, label]) => (
                <span key={label}>
                  <img src={src} alt="" />
                  {label}
                </span>
              ))}
            </div>
            <address>
              {t('footerAddress')}
              <br />
              {t('footerPhone')}
              <br />
              cancelaria@maia.gov.md
            </address>
          </div>

          <FooterColumn
            heading={t('footerColPlatform')}
            links={[
              [SITE_PAGE.presentation, t('footerLinkPresentation')],
              ['/consilieri', t('footerLinkAdvisors')],
              ['/instruiri', t('footerLinkTrainings')],
              ['/evenimente', t('footerLinkEvents')],
              ['/stiri', t('footerLinkNews')],
            ]}
          />
          <FooterColumn
            heading={t('footerColResources')}
            links={[
              ['/biblioteca', t('footerLinkLibrary')],
              ['/cercetare', t('footerLinkResearch')],
              ['/finantare', t('footerLinkFunding')],
              ['/comunitate', t('footerLinkCommunity')],
              ['/harta', t('navMap')],
            ]}
          />
          <FooterColumn
            heading={t('footerColParticipants')}
            links={[
              ['/cercetare', t('footerLinkResearchOrgs')],
              ['/instruiri/institutii', t('footerLinkTrainingInstitutions')],
              ['/comunitate', t('footerLinkAssociations')],
              ['/agentii-sprijin', t('footerLinkSupportAgencies')],
              ['/servicii', t('footerLinkAdvisoryServices')],
            ]}
          />
          <FooterColumn
            heading={t('footerColAssistance')}
            links={[
              ['/contact', t('footerLinkContactForm')],
              ['/cautare', t('navSearch')],
              ['/sitemap', t('navSitemap')],
            ]}
          />
        </div>

        <div className="footer-agencies">
          <div className="footer-agencies__logos">
            <img src="/img/gov/age.svg" alt={t('footerAgencyAgeAlt')} />
            <img src="/img/gov/gov.svg" alt={t('footerAgencyGovAlt')} />
          </div>
          <div className="footer-agencies__social">
            {SOCIAL.map(([href, label, icon]) => (
              <a key={label} href={href} target="_blank" rel="noopener" aria-label={label}>
                <img src={icon} alt="" />
              </a>
            ))}
          </div>
        </div>

        <EuStrip />

        <p className="attribution">{t('footerMudAttribution')}</p>

        <div className="footer-bottom">
          <div>
            <Link to="/politici/utilizare">{t('footerBottomTerms')}</Link>
            <Link to="/politici/confidentialitate">{t('footerBottomPrivacy')}</Link>
            <Link to="/politici/cookies">{t('footerBottomCookies')}</Link>
            <Link to={SITE_PAGE.accessibility}>{t('navAccessibilityStatement')}</Link>
          </div>
          <span>{t('footerCopyright')}</span>
        </div>
      </div>
    </footer>
  );
}
